import { Component, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { ApiPartnerEnum } from '@claas-rdv/rdv-client';
import { firstValueFrom } from 'rxjs';
import { AuthWrapperService } from '../services/auth-wrapper.service';
import { isApiPartner } from '../models/partner-system.model';
import { LANGUAGE_QUERY_PARAM, LanguageService } from '../services/language.service';

/**
 * Handles the Native-to-Web SSO entry point for both Okta and Auth0.
 *
 * This component is the target of two routes:
 * - `/security/okta/login`       → Okta  (SOFF / Händler)
 * - `/security/auth0/login`       → Auth0 (CC3 / Kunden)
 *
 * ## Okta Flow (Session Token Exchange):
 * The `/security/okta/login` route serves DOUBLE DUTY:
 * - It's the SSO entry point (native app opens it with ?sessionToken=...)
 * - It's the Okta redirectUri (Okta redirects back here with ?code=&state=...)
 *
 * This avoids the need for a separate /login/callback route and the
 * restoreOriginalUri loop that caused issues in previous implementations.
 *
 * Case 1 — Okta callback (?code=&state=):
 *   1. Okta redirected back with an authorization code
 *   2. `handleOktaCallback()` exchanges the code for tokens via parseFromUrl()
 *   3. The stored originalUri (set during exchangeOktaSessionToken) contains
 *      machine and autoConnect as query params
 *   4. Navigate to DisplayViewComponent with jwt, machine, autoConnect, apiPartner
 *
 * Case 2 — Native app entry (?sessionToken=...&machine=...&autoConnect=...):
 *   1. Build originalUri preserving machine and autoConnect (but NOT sessionToken)
 *   2. Call exchangeOktaSessionToken() which redirects to Okta /authorize
 *   3. Page unloads — flow continues when Okta redirects back (Case 1)
 *
 * Case 3 — Already authenticated (no special params):
 *   1. Check if user already has a valid Okta session
 *   2. If yes, get the access token and navigate to DisplayView
 *
 * Case 4 — Fallback:
 *   1. No sessionToken, no callback, not authenticated
 *   2. Build originalUri preserving machine and autoConnect (same as Case 2)
 *   3. Trigger a full Okta login redirect with that originalUri
 *   4. After redirect back, Case 1 restores machine and autoConnect
 *
 * ## Auth0 Flow (Native-to-Web SSO via Session Transfer Token or SSO Cookie):
 *
 * Case 1 — Session Transfer Token (?session_transfer_token=...&organization=...):
 *   1. Native app opens: /security/auth0/login?session_transfer_token=...&organization=...&machine=...&autoConnect=true
 *   2. InboundComponent reads session_transfer_token and organization from URL
 *   3. getAuth0TokenSilently({ session_transfer_token, organization }) is called
 *   4. Auth0 validates the session transfer token, establishes a web session,
 *      and returns an access token without prompting the user
 *   5. Navigate to DisplayViewComponent with jwt, machine, autoConnect, organization
 *
 * Case 2 — Auth0 redirect callback (?code=&state=):
 *   1. Auth0 redirected back after loginWithRedirect() was used as fallback
 *   2. Auth0 SDK processes the callback and sets isAuthenticated
 *   3. machine, autoConnect, and organization are recovered from appState
 *      (stored during the redirect in Case 4); the current URL only has
 *      ?code=&state= so the URL params cannot be used here
 *   4. Navigate to DisplayViewComponent with restored params
 *
 * Case 3 — SSO Cookie (no session_transfer_token in URL):
 *   1. Native app opened the web app without a session transfer token
 *   2. getAuth0TokenSilently() is called without extra params
 *   3. Auth0 uses the shared SSO cookie to authenticate silently
 *
 * Case 4 — Fallback (login_required):
 *   1. No session transfer token, no SSO cookie → loginWithRedirect()
 *   2. organization is forwarded via authorizationParams and preserved in appState
 *      so it survives the full redirect round-trip
 *
 * ## Language
 *
 * `lang` is preserved across the redirect exactly like machine/autoConnect —
 * via originalUri (Okta) and appState (Auth0). This is necessary because the
 * callback URL only carries ?code=&state=, so the language resolved at
 * bootstrap (see app.config.ts) has already fallen back to the browser
 * preference by the time we get here. Once restored, `LanguageService.apply()`
 * switches the UI, and `lang` is forwarded to /rdv so reloads and bookmarks
 * keep it.
 */
@Component({
  selector: 'app-inbound-component',
  imports: [],
  templateUrl: './inbound-component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './inbound-component.scss',
})
export class InboundComponent implements OnInit {
  private authWrapperService = inject(AuthWrapperService);
  private auth0AuthService = inject(AuthService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private languageService = inject(LanguageService);

  /** Loading / error state for the template */
  status = signal<'loading' | 'error'>('loading');
  errorMessage = signal('');

  async ngOnInit(): Promise<void> {
    try {
      // 1. Determine partner system from route resolver
      const sys = this.route.snapshot.data['resolvedPartnerSystem'];
      if (!isApiPartner(sys)) {
        this.setError(`Unknown partner system: "${sys}". Expected "${ApiPartnerEnum.SOFF}" or "${ApiPartnerEnum.CC3}".`);
        return;
      }
      const apiPartner: ApiPartnerEnum = sys;

      // 2. Read common query params (preserved across redirects via originalUri / appState)
      const machine = this.route.snapshot.queryParamMap.get('machine') ?? '';
      const autoConnect = this.route.snapshot.queryParamMap.get('autoConnect') ?? 'false';
      const lang = this.route.snapshot.queryParamMap.get(LANGUAGE_QUERY_PARAM) ?? '';

      // 3. Dispatch to the appropriate auth flow
      let result: { accessToken: string; organization: string } | null = null;

      if (apiPartner === ApiPartnerEnum.SOFF) {
        const accessToken = await this.handleOkta(machine, autoConnect, lang);
        if (accessToken) {
          result = { accessToken, organization: '' };
        }
      } else {
        result = await this.handleAuth0(machine, autoConnect, lang);
      }

      // 4. If we got a token, navigate to the display view
      if (result) {
        this.navigateToDisplayView(result.accessToken, machine, autoConnect, apiPartner, result.organization, lang);
      }
      // If null, a redirect is in progress (page will reload)
    } catch (err) {
      console.error('InboundComponent: authentication failed', err);
      this.setError('Authentication failed. Please try again.');
    }
  }

  // ---------------------------------------------------------------------------
  // Okta: Session Token Exchange + Callback Handling
  // ---------------------------------------------------------------------------

  private async handleOkta(machine: string, autoConnect: string, lang: string): Promise<string | null> {
    // Case 1: Okta callback — URL has ?code=&state= from Okta /authorize redirect.
    // This happens after exchangeOktaSessionToken() redirected to Okta and Okta
    // redirected back to this same route with the authorization code.
    if (this.authWrapperService.isOktaCallback()) {
      const { accessToken, originalUri } = await this.authWrapperService.handleOktaCallback();

      // The originalUri was stored by exchangeOktaSessionToken() and contains
      // machine, autoConnect and lang as query params. Extract them so we can
      // pass them to the display view (the current URL params include
      // code/state from Okta, not the original ones).
      const { machine: origMachine, autoConnect: origAutoConnect, lang: origLang } =
        this.parseOriginalUri(originalUri);

      // Bootstrap resolved the language without the lang param (the callback URL
      // has none), so switch now that the original choice is known again.
      const restoredLang = this.languageService.apply(origLang || lang);

      // Navigate directly — use params from originalUri since current URL has code/state
      this.navigateToDisplayView(
        accessToken,
        origMachine || machine,
        origAutoConnect || autoConnect,
        ApiPartnerEnum.SOFF,
        '',
        restoredLang
      );
      return null; // Navigation already handled
    }

    // Case 2: Coming from native app with a sessionToken → exchange it
    const sessionToken = this.route.snapshot.queryParamMap.get('sessionToken');
    if (sessionToken) {
      // Build the originalUri preserving machine, autoConnect and lang (but NOT
      // sessionToken). After Okta redirects back with ?code=&state=, Case 1
      // above will handle it and restore them from this stored originalUri.
      const originalUri = this.buildOriginalUri(machine, autoConnect, lang);
      await this.authWrapperService.exchangeOktaSessionToken(sessionToken, originalUri);
      // Browser will redirect to Okta → this code path ends here
      return null;
    }

    // Case 2b: Coming with an interclient_token → exchange it via Okta /authorize
    const interclientToken = this.route.snapshot.queryParamMap.get('interclient_token');
    if (interclientToken) {
      const originalUri = this.buildOriginalUri(machine, autoConnect, lang);
      await this.authWrapperService.exchangeOktaInterclientToken(interclientToken, originalUri);
      // Browser will redirect to Okta → this code path ends here
      return null;
    }

    // Case 3: No callback, no sessionToken, no interclientToken — check if already authenticated
    const existingToken = await this.authWrapperService.getOktaTokenIfAuthenticated();
    if (existingToken) {
      return existingToken;
    }

    // Case 4: Fallback — trigger a full Okta login redirect
    // Build originalUri so machine, autoConnect and lang survive the redirect
    // round-trip, same as Case 2. After Okta redirects back, Case 1 restores them.
    console.warn('InboundComponent: No Okta sessionToken, no callback, not authenticated — falling back to login redirect');
    const originalUri = this.buildOriginalUri(machine, autoConnect, lang);
    await this.authWrapperService.loginWithRedirect(ApiPartnerEnum.SOFF, undefined, undefined, originalUri);
    return null;
  }

  // ---------------------------------------------------------------------------
  // Auth0: Session Transfer Token + SSO Cookie + Fallback
  // ---------------------------------------------------------------------------

  private async handleAuth0(machine: string, autoConnect: string, lang: string): Promise<{ accessToken: string; organization: string } | null> {
    // Case 1: Returning from an Auth0 redirect callback (?code=...&state=...)
    const code = this.route.snapshot.queryParamMap.get('code');
    const state = this.route.snapshot.queryParamMap.get('state');

    if (code && state) {
      // Auth0 SDK handles the callback internally when AuthModule is loaded.
      // We wait for the authenticated state and then get the token.
      // machine, autoConnect, lang and organization are recovered from appState,
      // where they were stored before the redirect in Case 4 below.
      // NOTE: the current URL only has ?code=&state= at this point, so
      // machine/autoConnect/lang from the outer scope are empty — we must use appState.
      try {
        const isAuthenticated = await firstValueFrom(this.auth0AuthService.isAuthenticated$);
        if (isAuthenticated) {
          const appState = await firstValueFrom(this.auth0AuthService.appState$);
          const organization: string = appState?.['organization'] ?? '';
          const restoredMachine: string = appState?.['machine'] ?? machine;
          const restoredAutoConnect: string = appState?.['autoConnect'] ?? autoConnect;
          const accessToken = await this.authWrapperService.getAuth0TokenSilently(
            organization ? { organization } : undefined
          );
          // Bootstrap resolved the language without the lang param (the callback
          // URL has none), so switch now that the original choice is known again.
          const restoredLang = this.languageService.apply(appState?.[LANGUAGE_QUERY_PARAM] ?? lang);
          // Navigate directly here so we use the restored params from appState
          this.navigateToDisplayView(accessToken, restoredMachine, restoredAutoConnect, ApiPartnerEnum.CC3, organization, restoredLang);
          return null; // Navigation already handled
        }
      } catch (err) {
        console.error('InboundComponent: Auth0 callback processing failed', err);
      }
    }

    // Read Auth0-specific SSO params from the URL.
    // These are set by the native app when opening the web app for Native-to-Web SSO.
    const sessionTransferToken = this.route.snapshot.queryParamMap.get('session_transfer_token') ?? '';
    const organization = this.route.snapshot.queryParamMap.get('organization') ?? '';

    // Build authorizationParams with only the non-empty values.
    // - session_transfer_token: present when native app initiates Native-to-Web SSO
    // - organization: present when the native app authenticated within an Auth0 org;
    //   MUST match the org embedded in the session_transfer_token (Auth0 requirement)
    const authorizationParams: Record<string, string> = {};
    if (sessionTransferToken) authorizationParams['session_transfer_token'] = sessionTransferToken;
    if (organization) authorizationParams['organization'] = organization;

    // Case 2 / 3: Try silent auth.
    // - With session_transfer_token: Auth0 exchanges it for a session (Native-to-Web SSO)
    // - Without session_transfer_token: Auth0 uses the shared SSO cookie (existing flow)
    // - organization (if present) is forwarded to satisfy Auth0's org-matching requirement
    try {
      const accessToken = await this.authWrapperService.getAuth0TokenSilently(
        Object.keys(authorizationParams).length > 0 ? authorizationParams : undefined
      );
      return { accessToken, organization };
    } catch (err: any) {
      if (err?.error === 'login_required' || err?.error === 'consent_required') {
        // Case 4: No SSO session and no valid session transfer token →
        // fall back to a full Auth0 login redirect.
        // - organization is passed via authorizationParams so Auth0 scopes the login
        //   to the correct org from the start
        // - organization is also stored in appState so it survives the redirect and
        //   can be recovered in Case 1 above when Auth0 sends back ?code=&state=
        // - lang rides along in appState for the same reason (the callback URL
        //   carries only ?code=&state=, so an explicit ?lang= would be lost)
        // - session_transfer_token is intentionally NOT forwarded here: it's single-use
        //   and was already consumed (or is absent), so including it would cause an error
        console.warn('InboundComponent: No Auth0 SSO session, falling back to login redirect');
        const redirectAuthorizationParams: Record<string, string> = {};
        if (organization) redirectAuthorizationParams['organization'] = organization;

        await this.authWrapperService.loginWithRedirect(
          ApiPartnerEnum.CC3,
          {
            machine,
            autoConnect,
            returnTo: window.location.pathname,
            ...(organization ? { organization } : {}),
            ...(lang ? { [LANGUAGE_QUERY_PARAM]: lang } : {}),
          },
          Object.keys(redirectAuthorizationParams).length > 0 ? redirectAuthorizationParams : undefined
        );
        return null;
      }
      // Unexpected error
      throw err;
    }
  }

  // ---------------------------------------------------------------------------
  // Navigation
  // ---------------------------------------------------------------------------

  private navigateToDisplayView(
    accessToken: string,
    machine: string,
    autoConnect: string,
    apiPartner: ApiPartnerEnum,
    organization: string,
    lang: string
  ): void {
    this.router.navigate(['/rdv'], {
      queryParams: {
        jwt: accessToken,
        machine,
        autoConnect,
        apiPartner,
        ...(organization ? { organization } : {}),
        // Keeps the language across reloads, bookmarks and "open in new tab"
        ...(lang ? { [LANGUAGE_QUERY_PARAM]: lang } : {}),
      },
      replaceUrl: true, // Remove token from browser history
    });
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /**
   * Builds the originalUri for Okta's signInWithRedirect() to store.
   * This preserves machine, autoConnect and lang across the Okta redirect.
   * Does NOT include sessionToken (it's single-use and consumed).
   */
  private buildOriginalUri(machine: string, autoConnect: string, lang: string): string {
    const path = this.route.snapshot.url.map((s) => s.path).join('/');
    const params = new URLSearchParams();
    if (machine) params.set('machine', machine);
    if (autoConnect) params.set('autoConnect', autoConnect);
    if (lang) params.set(LANGUAGE_QUERY_PARAM, lang);
    const query = params.toString();
    return `/${path}${query ? '?' + query : ''}`;
  }

  /**
   * Parses machine, autoConnect and lang from the stored originalUri.
   * The originalUri is a relative path like:
   *   /security/okta/login?machine=123&autoConnect=true&lang=fr
   */
  private parseOriginalUri(originalUri: string | undefined): { machine: string; autoConnect: string; lang: string } {
    if (!originalUri) {
      return { machine: '', autoConnect: 'false', lang: '' };
    }
    try {
      // originalUri is a relative path — use a dummy base to parse it
      const url = new URL(originalUri, 'http://dummy');
      return {
        machine: url.searchParams.get('machine') ?? '',
        autoConnect: url.searchParams.get('autoConnect') ?? 'false',
        lang: url.searchParams.get(LANGUAGE_QUERY_PARAM) ?? '',
      };
    } catch {
      console.warn('InboundComponent: Failed to parse originalUri:', originalUri);
      return { machine: '', autoConnect: 'false', lang: '' };
    }
  }

  private setError(message: string): void {
    this.status.set('error');
    this.errorMessage.set(message);
    console.error('InboundComponent:', message);
  }
}
